import { useState, FormEvent, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageHeroDecor from '@/components/feature/PageHeroDecor';
import { FadeIn, LineReveal } from '@/components/feature/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';

const inquiryTypes = [
  '30分相談（無料）',
  '資料請求',
  'プロジェクトのご相談',
  'その他お問い合わせ',
];

const domainOptions = [
  '医療',
  '福祉',
  '教育',
  '地域',
  '文化',
  'その他',
];



export default function ContactPage() {
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);

  const toggleDomain = (domain: string) => {
    setSelectedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain]
    );
  };

  useSEO({
    title: 'お問い合わせ・無料相談 | メセナインターコム株式会社',
    description: 'メセナインターコム株式会社へのお問い合わせ・無料相談はこちら。医療・福祉・教育・地域・文化の意味設計・価値創造についてのご相談を30分無料で承ります。課題が明確でなくても構いません。',
    keywords: 'お問い合わせ,無料相談,意味設計,コンサルティング,医療,福祉,教育,地域,文化',
    ogTitle: 'お問い合わせ・無料相談 | メセナインターコム株式会社',
    ogDescription: 'メセナインターコム株式会社へのお問い合わせ・無料相談はこちら。30分の対話から、組織の意味を一緒に整理します。',
    ogUrl: 'https://mecena-intercom.co.jp/contact',
    canonical: 'https://mecena-intercom.co.jp/contact',
  });

  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
    });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setStatus('sending');
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      const res = await fetch('https://formspree.io/f/xjgjepba', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-warm-white min-h-screen">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-40 pb-20 md:pt-52 md:pb-28 bg-warm-white relative overflow-hidden">
          <PageHeroDecor variant="contact" />
          <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div
                ref={heroRef}
                className="md:col-span-8 md:col-start-3"
                style={{ opacity: 0, transform: 'translateY(32px)', transition: 'opacity 0.9s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.9s cubic-bezier(0.22,1,0.36,1) 0.1s' }}
              >
                <div className="flex items-center gap-4 mb-10">
                  <LineReveal delay={0.3} color="bg-accent-teal" className="w-6" />
                  <span className="text-[10px] font-sans-jp text-accent-teal tracking-widest-jp uppercase">Contact</span>
                </div>
                <h1 className="font-serif-jp font-light text-charcoal tracking-normal-jp leading-tight text-4xl md:text-5xl mb-12">
                  まずは、現状の「意味」を<br />一緒に整理しましょう。
                </h1>
                <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp max-w-xl">
                  課題が明確でなくても構いません。<br />
                  「何かが変わらなければならない」という感覚があれば、<br />
                  それが出発点になります。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Form + Info */}
        <section className="texture-contact py-16 md:py-24 bg-warm-light relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
              {/* Left info */}
              <FadeIn delay={0.1} className="md:col-span-4">
                <div className="space-y-12">
                  <div>
                    <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-6">30分で整理できること</p>
                    <div className="space-y-0 border-t border-divider">
                      {['今、何が散らばっているか', 'どこを一旦の完成形とするか', '何から着手すべきか'].map((item) => (
                        <div key={item} className="flex items-center gap-4 py-4 border-b border-divider">
                          <span className="block w-1 h-1 rounded-full bg-accent-teal flex-shrink-0" />
                          <span className="font-serif-jp text-sm font-light text-charcoal tracking-normal-jp">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-sans-jp text-muted-light tracking-widest-jp uppercase mb-6">こんな方からのご連絡をお待ちしています</p>
                    <div className="space-y-3">
                      {[
                        '価値が伝わっていないと感じている',
                        '発信しているのに反応がない',
                        '組織の言葉を整理したい',
                        '長期的なパートナーを探している',
                      ].map((item) => (
                        <div key={item} className="flex items-start gap-3">
                          <span className="block w-1 h-1 rounded-full bg-accent-teal flex-shrink-0 mt-2" />
                          <span className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pt-6 border-t border-divider">
                    <blockquote className="font-garamond text-base italic text-muted-brown leading-relaxed">
                      &ldquo;The meaning is already there.<br />We just help you see it.&rdquo;
                    </blockquote>
                  </div>
                </div>
              </FadeIn>

              {/* Right form */}
              <FadeIn delay={0.25} className="md:col-span-8">
                {status === 'success' ? (
                  <div className="py-20 text-center">
                    <div className="w-12 h-12 flex items-center justify-center border border-accent-teal mx-auto mb-8">
                      <i className="ri-check-line text-accent-teal text-xl" />
                    </div>
                    <h3 className="font-serif-jp font-light text-charcoal tracking-normal-jp text-2xl mb-4">
                      お問い合わせありがとうございます。
                    </h3>
                    <p className="font-sans-jp text-sm text-muted-brown leading-relaxed-jp">
                      担当者が内容を確認次第、改めてご連絡いたします。
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="mt-10 inline-flex items-center gap-3 px-10 py-4 border border-charcoal text-charcoal text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal hover:text-warm-white transition-colors duration-300 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-base" />
                      トップへ戻る
                    </button>
                  </div>
                ) : (
                  <form
                    id="contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-8"
                  >
                    {/* Inquiry type */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-4">
                        お問い合わせの種類 <span className="text-accent-teal">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {inquiryTypes.map((type) => (
                          <label key={type} className="flex items-center gap-3 cursor-pointer group">
                            <span className="relative w-4 h-4 flex-shrink-0">
                              <input
                                type="radio"
                                name="inquiry_type"
                                value={type}
                                required
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                onChange={() => {}}
                              />
                              <span className="pointer-events-none w-4 h-4 border border-divider flex items-center justify-center group-has-[input:checked]:border-accent-teal transition-colors duration-200 block">
                                <span className="w-2 h-2 bg-accent-teal hidden group-has-[input:checked]:block" />
                              </span>
                            </span>
                            <span className="font-sans-jp text-sm text-muted-brown group-hover:text-charcoal transition-colors duration-200">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Domain field */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-4">
                        領域 <span className="text-accent-teal">*</span>
                      </label>
                      <input
                        type="hidden"
                        name="domain"
                        value={selectedDomains.join('・')}
                      />
                      <div className="grid grid-cols-3 gap-3">
                        {domainOptions.map((domain) => {
                          const checked = selectedDomains.includes(domain);
                          return (
                            <label key={domain} className="flex items-center gap-3 cursor-pointer group">
                              <span className="relative w-4 h-4 flex-shrink-0">
                                <input
                                  type="checkbox"
                                  value={domain}
                                  checked={checked}
                                  onChange={() => toggleDomain(domain)}
                                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <span className={`pointer-events-none w-4 h-4 border flex items-center justify-center transition-colors duration-200 block ${checked ? 'border-accent-teal' : 'border-divider'}`}>
                                  {checked && <span className="w-2 h-2 bg-accent-teal block" />}
                                </span>
                              </span>
                              <span className={`font-sans-jp text-sm transition-colors duration-200 ${checked ? 'text-charcoal' : 'text-muted-brown group-hover:text-charcoal'}`}>{domain}</span>
                            </label>
                          );
                        })}
                      </div>

                      {/* 「その他」選択時の自由記述欄 */}
                      {selectedDomains.includes('その他') && (
                        <div
                          className="mt-4 overflow-hidden"
                          style={{ animation: 'fadeSlideIn 0.3s cubic-bezier(0.22,1,0.36,1) both' }}
                        >
                          <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                          <input
                            type="text"
                            name="domain_other"
                            placeholder="領域をご記入ください"
                            className="w-full border-b border-divider bg-transparent py-3 text-sm font-sans-jp text-charcoal placeholder:text-muted-light focus:outline-none focus:border-accent-teal transition-colors duration-200"
                          />
                        </div>
                      )}
                    </div>

                    {/* Organization field */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">
                        組織・団体名 <span className="text-accent-teal">*</span>
                      </label>
                      <input
                        type="text"
                        name="organization"
                        required
                        placeholder="例：○○医療法人、△△学校法人"
                        className="w-full border-b border-divider bg-transparent py-3 text-sm font-sans-jp text-charcoal placeholder:text-muted-light focus:outline-none focus:border-accent-teal transition-colors duration-200"
                      />
                    </div>

                    {/* Name */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">
                        お名前 <span className="text-accent-teal">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="例：山田 太郎"
                        className="w-full border-b border-divider bg-transparent py-3 text-sm font-sans-jp text-charcoal placeholder:text-muted-light focus:outline-none focus:border-accent-teal transition-colors duration-200"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">
                        メールアドレス <span className="text-accent-teal">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="例：taro@example.com"
                        className="w-full border-b border-divider bg-transparent py-3 text-sm font-sans-jp text-charcoal placeholder:text-muted-light focus:outline-none focus:border-accent-teal transition-colors duration-200"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-sans-jp text-muted-light tracking-widest-jp uppercase mb-3">
                        現状・課題・ご相談内容
                      </label>
                      <textarea
                        name="message"
                        rows={6}
                        maxLength={500}
                        placeholder="現在の課題や、ご相談したいことを自由にお書きください。まだ整理できていなくても構いません。"
                        onChange={(e) => setCharCount(e.target.value.length)}
                        className="w-full border-b border-divider bg-transparent py-3 text-sm font-sans-jp text-charcoal placeholder:text-muted-light focus:outline-none focus:border-accent-teal transition-colors duration-200 resize-none"
                      />
                      <div className="flex justify-end mt-1">
                        <span className={`text-xs font-sans-jp ${charCount > 500 ? 'text-red-500' : 'text-muted-light'}`}>
                          {charCount} / 500
                        </span>
                      </div>
                    </div>

                    {/* Submit */}
                    {status === 'error' && (
                      <p className="font-sans-jp text-sm text-red-500">
                        送信に失敗しました。時間をおいて再度お試しください。
                      </p>
                    )}
                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={status === 'sending' || charCount > 500}
                        className="inline-flex items-center gap-3 px-10 py-4 bg-charcoal text-warm-white text-sm font-sans-jp tracking-wide-jp hover:bg-charcoal-soft transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {status === 'sending' ? '送信中...' : '送信する'}
                      </button>
                    </div>
                  </form>
                )}
              </FadeIn>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
